<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :formsConfig="formsConfig"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentStatic title="Features" />
    </template>

    <DxToolbar class="view-toolbar">
      <DxItem
        :options="toolbar.edit"
        location="after"
        locate-in-menu="auto"
        widget="dxButton"
        v-if="baseView.mode === 'review'"
      />
      <DxItem
        :options="toolbar.save"
        location="after"
        locate-in-menu="auto"
        widget="dxButton"
        v-if="baseView.mode !== 'review'"
      />
    </DxToolbar>

    <div class="fa-layout">
      <!-- Left sidebar: Work Centres -->
      <div class="fa-sidebar">
        <div
          v-for="wc in workCentres"
          :key="wc.WorkCentre"
          :class="['fa-wc-item', { 'fa-wc-item--active': activeWc === wc.WorkCentre }]"
          @click="scrollToWc(wc.WorkCentre)"
        >
          <DxCheckBox
            :value="workCentreCheckValue(wc)"
            @value-changed="onWorkCentreToggle(wc, $event)"
            @click.native="$event.stopPropagation()"
            :read-only="baseView.mode === 'review'"
          />
          <span class="fa-wc-name">{{ wc.WorkCentre }}</span>
        </div>
      </div>

      <!-- Right content area -->
      <div class="fa-content" ref="content">
        <!-- Search bar -->
        <div class="fa-search-bar">
          <DxTextBox
            v-model="searchQuery"
            placeholder="Search..."
            :show-clear-button="true"
            styling-mode="outlined"
          />
        </div>

        <!-- Modules for the selected work centre -->
        <div class="fa-grid" v-if="activeWorkCentre">
          <div
            v-for="mod in visibleModules(activeWorkCentre)"
            :key="mod.Module"
            class="fa-module-card"
          >
            <div
              class="fa-module-header"
              @click="toggleCollapsed(activeWorkCentre.WorkCentre, mod.Module)"
            >
              <DxCheckBox
                :value="moduleCheckValue(mod)"
                @value-changed="onModuleToggle(mod, $event)"
                @click.native="$event.stopPropagation()"
                :read-only="baseView.mode === 'review'"
              />
              <span class="fa-module-title">{{ mod.Module }}</span>
              <i
                :class="
                  isCollapsed(activeWorkCentre.WorkCentre, mod.Module)
                    ? 'dx-icon-chevronnext'
                    : 'dx-icon-chevrondown'
                "
                class="fa-chevron"
              />
            </div>

            <div
              v-if="!isCollapsed(activeWorkCentre.WorkCentre, mod.Module)"
              class="fa-module-features"
            >
              <div
                v-for="feat in visibleFeatures(mod)"
                :key="feat.FeatureCode"
                class="fa-feature-row"
              >
                <span class="fa-feature-name">{{ feat.DisplayName }}</span>
                <DxCheckBox
                  :value="featureState[feat.FeatureCode]"
                  @value-changed="onFeatureToggle(feat.FeatureCode, $event)"
                  :read-only="baseView.mode === 'review'"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="!formsConfig.loadingVisible && activeWorkCentre && visibleModules(activeWorkCentre).length === 0" class="fa-empty">
          No features match your search.
        </div>
        <div v-if="!formsConfig.loadingVisible && workCentres.length === 0" class="fa-empty">
          No features found.
        </div>
      </div>
    </div>
  </gc-main-layout>
</template>

<script>
import BaseViewMixin from "../../../v1/_mixins/base.view";
import { FeatureAvailabilityService } from "../../../_services/api/settings/graycorp/feature-availability.service";
import { TokenStorage } from "../../../_services/storage";

export default {
  mixins: [BaseViewMixin],
  data() {
    return {
      workCentres: [],
      featureState: {},
      collapsed: {},
      searchQuery: "",
      activeWc: null,
      customerCode: TokenStorage.getTenant(),

      toolbar: {
        edit: {
          icon: "edit",
          onClick: () => {
            this.$router.push({ path: "/feature-availability/edit" });
          },
        },
        save: {
          icon: "floppy",
          onClick: () => {
            this.$logger.trackEventButtonClickSave();
            this.save();
          },
        },
      },
    };
  },
  computed: {
    activeWorkCentre() {
      return this.workCentres.find((wc) => wc.WorkCentre === this.activeWc) || null;
    },
  },
  methods: {
    // ── Data loading ────────────────────────────────────────────────

    load() {
      if (!this.customerCode) return;
      this.showLoading(true);
      FeatureAvailabilityService.getFeatureAvailability(this.customerCode)
        .then((response) => {
          this.workCentres = response.data;
          if (this.workCentres.length) {
            this.activeWc = this.workCentres[0].WorkCentre;
          }
          const state = {};
          for (const wc of this.workCentres) {
            for (const mod of wc.Modules) {
              for (const feat of mod.Features) {
                state[feat.FeatureCode] = feat.IsEnabled;
              }
            }
          }
          this.featureState = state;
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to load feature availability");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Save ─────────────────────────────────────────────────────────

    save() {
      this.showLoading(true);
      const changes = Object.entries(this.featureState).map(
        ([featureCode, isEnabled]) => ({ FeatureCode: featureCode, IsEnabled: isEnabled })
      );
      FeatureAvailabilityService.saveFeatureAvailability(this.customerCode, changes)
        .then(() => {
          this.$notify("Feature availability saved successfully", "success");
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({ path: "/feature-availability" });
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to save feature availability");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    backButtonFunction() {
      this.$router.push({ path: "/settings/user" });
    },

    // ── Sidebar navigation ───────────────────────────────────────────

    scrollToWc(wcName) {
      this.activeWc = wcName;
    },

    // ── Visibility helpers ───────────────────────────────────────────

    visibleModules(wc) {
      return wc.Modules.filter((mod) => this.visibleFeatures(mod).length > 0);
    },

    visibleFeatures(mod) {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return mod.Features;
      return mod.Features.filter((f) =>
        f.DisplayName.toLowerCase().includes(q)
      );
    },

    // ── Collapse ─────────────────────────────────────────────────────

    toggleCollapsed(wcName, moduleName) {
      const key = `${wcName}|${moduleName}`;
      this.$set(this.collapsed, key, !this.collapsed[key]);
    },

    isCollapsed(wcName, moduleName) {
      return !!this.collapsed[`${wcName}|${moduleName}`];
    },

    // ── Checkbox state helpers ────────────────────────────────────────

    workCentreCheckValue(wc) {
      const features = wc.Modules.flatMap((m) => m.Features);
      if (!features.length) return false;
      const enabledCount = features.filter(
        (f) => this.featureState[f.FeatureCode]
      ).length;
      if (enabledCount === 0) return false;
      if (enabledCount === features.length) return true;
      return null; // indeterminate
    },

    moduleCheckValue(mod) {
      if (!mod.Features.length) return false;
      const enabledCount = mod.Features.filter(
        (f) => this.featureState[f.FeatureCode]
      ).length;
      if (enabledCount === 0) return false;
      if (enabledCount === mod.Features.length) return true;
      return null; // indeterminate
    },

    // ── Toggle handlers ───────────────────────────────────────────────

    onFeatureToggle(featureCode, event) {
      this.$set(this.featureState, featureCode, event.value);
    },

    onModuleToggle(mod, event) {
      // event.event is the original DOM event — only present on user-initiated changes.
      // Ignore programmatic updates (e.g. when moduleCheckValue recomputes after a feature toggle).
      if (!event.event) return;
      const newVal = this.moduleCheckValue(mod) !== true;
      for (const feat of mod.Features) {
        this.$set(this.featureState, feat.FeatureCode, newVal);
      }
    },

    onWorkCentreToggle(wc, event) {
      if (!event.event) return;
      const newVal = this.workCentreCheckValue(wc) !== true;
      for (const mod of wc.Modules) {
        for (const feat of mod.Features) {
          this.$set(this.featureState, feat.FeatureCode, newVal);
        }
      }
    },
  },
  created() {
    this.toolbar.edit.visible = this.formsConfig.editableOption;
    this.load();
  },
};
</script>

<style scoped lang="scss">
.fa-layout {
  display: flex;
  height: 100%;
  overflow: hidden;
}

// ── Left sidebar ──────────────────────────────────────────────────────
.fa-sidebar {
  width: 180px;
  min-width: 180px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  padding: 12px 0;
  background: #fff;
}

.fa-wc-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f5f5f5;
  }

  &--active {
    background: #e8f0fe;

    .fa-wc-name {
      color: var(--base-accent);
      font-weight: 500;
    }
  }
}

.fa-wc-name {
  font-size: 13px;
  line-height: 1.4;
}

// ── Right content ─────────────────────────────────────────────────────
.fa-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f9f9f9;
}

.fa-search-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  .dx-textbox {
    width: 280px;
  }
}

// ── 2-column grid ─────────────────────────────────────────────────────
.fa-grid {
  columns: 2;
  column-gap: 16px;
}

.fa-wc-section {
  break-inside: avoid;
  margin-bottom: 4px;
}

// ── Module card ───────────────────────────────────────────────────────
.fa-module-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  overflow: hidden;
  break-inside: avoid;
}

.fa-module-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  background: #fafafa;

  &:hover {
    background: #f0f0f0;
  }
}

.fa-module-title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
}

.fa-chevron {
  font-size: 11px;
  color: #999;
}

// ── Feature rows ──────────────────────────────────────────────────────
.fa-module-features {
  border-top: 1px solid #f0f0f0;
}

.fa-feature-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
}

.fa-feature-name {
  font-size: 13px;
  flex: 1;
  padding-right: 12px;
}

// ── Empty state ───────────────────────────────────────────────────────
.fa-empty {
  text-align: center;
  padding: 48px;
  color: #999;
  font-size: 14px;
}
</style>
